import * as React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { api } from '@/lib/api'
import { useAuthStore } from '@/store/auth'

/**
 * 強制同意 modal：當 user.terms_acceptance_required === true 時遮罩整個 app。
 * - 「同意並繼續」：呼叫 POST /api/auth/terms/accept，成功後刷新 user 並關閉 modal
 * - 「不同意（登出）」：呼叫 clearAuth 登出
 * 不可關閉、不可 Esc / 點 backdrop 取消（強制行為）。
 */
export function TermsAcceptanceModal() {
  const { t } = useTranslation('legal')
  const user = useAuthStore((s) => s.user)
  const setAuth = useAuthStore((s) => s.setAuth)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const [agreed, setAgreed] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)

  if (!user || user.terms_acceptance_required !== true) return null

  async function handleAccept() {
    setSubmitting(true)
    try {
      await api.post('api/auth/terms/accept').json()
      // 樂觀更新 user 狀態
      if (user) setAuth({ ...user, terms_acceptance_required: false })
      toast.success(t('modal.accept'))
    } catch {
      toast.error('無法同意，請重試')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleLogout() {
    await clearAuth()
    window.location.href = '/login'
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: 'rgba(26,26,46,0.85)' }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full"
        style={{
          maxWidth: 520,
          background: 'var(--color-surface)',
          border: '2px solid var(--color-border)',
          boxShadow: '8px 8px 0 0 var(--color-border)',
        }}
      >
        {/* 頭 */}
        <div
          className="px-5 py-3 flex items-center gap-2 border-b-2 border-[var(--color-border)]"
          style={{ background: 'var(--color-sun)' }}
        >
          <span className="font-pixel text-xs" style={{ color: 'var(--color-foreground)' }}>
            ⚠ {t('modal.title')}
          </span>
        </div>

        {/* 內容 */}
        <div className="p-6">
          <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
            {t('modal.subtitle')}
          </p>

          <div
            className="inline-flex font-pixel text-xs mb-4 gap-2"
            style={{ fontSize: 9 }}
          >
            <span style={{ border: '2px solid var(--color-border)', padding: '3px 8px', background: 'var(--color-cream)', color: 'var(--color-foreground)' }}>
              {t('modal.version_label')}：v {t('version')}
            </span>
            <span style={{ border: '2px solid var(--color-border)', padding: '3px 8px', background: 'var(--color-cream)', color: 'var(--color-muted)' }}>
              {t('last_updated')}
            </span>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <Link
              to="/terms"
              target="_blank"
              className="font-pixel text-xs underline"
              style={{ color: 'var(--color-coral)', fontSize: 11, fontWeight: 700 }}
            >
              {t('modal.view_terms')} ↗
            </Link>
            <span style={{ color: 'var(--color-border)' }}>•</span>
            <Link
              to="/privacy"
              target="_blank"
              className="font-pixel text-xs underline"
              style={{ color: 'var(--color-navy)', fontSize: 11, fontWeight: 700 }}
            >
              {t('modal.view_privacy')} ↗
            </Link>
          </div>

          <label className="flex gap-2 items-start text-sm mb-5 cursor-pointer select-none" style={{ color: 'var(--color-foreground)' }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[var(--color-coral)]"
            />
            <span>{t('modal.checkbox')}</span>
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleLogout}
              disabled={submitting}
              className="flex-1 font-pixel disabled:opacity-60"
              style={{
                border: '2px solid var(--color-border)',
                background: 'var(--color-surface)',
                color: 'var(--color-foreground)',
                fontSize: 10,
                padding: 10,
                cursor: 'pointer',
                boxShadow: '2px 2px 0 0 var(--color-border)',
              }}
            >
              {t('modal.logout')}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!agreed || submitting}
              className="flex-1 font-pixel disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                border: '2px solid var(--color-border)',
                background: agreed ? 'var(--color-coral)' : 'var(--color-muted)',
                color: 'white',
                fontSize: 10,
                padding: 10,
                cursor: agreed ? 'pointer' : 'not-allowed',
                boxShadow: '2px 2px 0 0 var(--color-border)',
                letterSpacing: '0.06em',
              }}
            >
              {submitting ? '...' : t('modal.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsAcceptanceModal
